/** Contracts for opted-in mission review. Public projections must never include trace content. */
import type { VoiceMetricStage, VoicePipelineIdentity, VoiceStageUsage, VOICE_LATENCY_FIELDS } from './voice-diagnostics';
export const EVAL_CONSENT_VERSION = 'mission-review-v1';
export const PLAYER_FEEDBACK_CONSENT_VERSION = 'player-feedback-v1';
export const PLAYER_FEEDBACK_PROMPT_VERSION = 'moose-debrief-v1';
export const LIVE_DEBRIEF_PROMPT_VERSION = 'moose-live-debrief-v1';
export const MAX_PLAYER_FEEDBACK_LENGTH = 4000;
export const EVAL_RUBRIC_VERSION = 'moose-turn-v1';
export const EVAL_DIMENSIONS = ['commandCorrect', 'noUnrequestedAction', 'groundedInState', 'retainedIntent', 'characterAppropriate', 'conciseEnough'] as const;
export type EvalDimension = typeof EVAL_DIMENSIONS[number];
export type EvalValue = 'pass' | 'fail' | 'unknown';
export type MissionOutcome = 'complete' | 'failed' | 'abandoned';
export type ReviewStatus = 'capturing' | 'queued' | 'reviewing' | 'reviewed' | 'error' | 'unconfigured' | 'not-requested';
export interface EvalPlayerFeedback {
  /** Opaque client-reported run grouping; never authenticated trace evidence. */
  missionGroupId?: string;
  author: 'player'; text: string; source: 'typed' | 'dictated' | 'mixed'; feedbackPromptVersion: string;
  submittedAt: string; mode: 'practice' | 'voice'; outcome: MissionOutcome;
  context: {durationSeconds?: number; completedSites?: number; totalSites?: number};
}
/** Separate consent and storage identity: sending a debrief never opts into mission traces. */
export interface EvalPlayerFeedbackSubmission {
  /** Matches the captured mission identity when present; otherwise a fresh launch UUID. */
  missionGroupId?: string;
  missionId: string; writeToken: string; consentVersion: string; startedAt: string; endedAt: string;
  promptVersion: string; outcome: MissionOutcome; mode: EvalPlayerFeedback['mode'];
  text: string; source: EvalPlayerFeedback['source']; feedbackPromptVersion: string;
  context?: EvalPlayerFeedback['context'];
}
export interface EvalEvent {
  id: string; at: number; type: string; data: Record<string, unknown>; sessionId?: string; turnId?: string;
}
export interface EvalTurnReview {
  turnId: string; expected: string; observed: string; evidenceIds: string[];
  dimensions: Record<EvalDimension, EvalValue>;
}
export interface EvalReview {
  /** Older local reviews predate provider selection and used OpenAI. */
  provider?: 'openai' | 'openrouter';
  rubricVersion: string; model: string; resolvedModel: string | null; judgeInstructionDigest: string; reviewedAt: string; promptVersion: string;
  inputDigest: string; summary: string; turns: EvalTurnReview[];
  coverage: {totalPlayerTurns: number; reviewedTurns: number; omittedTurns: number; incompleteCapture: boolean; exactReplayAvailable: false; audioAvailable: false};
  usage: {inputTokens: number | null; outputTokens: number | null};
}
export interface EvalHumanLabel {
  id: string; turnId: string; dimension: EvalDimension; value: EvalValue; notes: string; createdAt: string;
}
export interface EvalMissionSummary {
  /** Absent on older records, which are all mission traces. */
  captureKind?: 'mission-trace' | 'player-feedback';
  missionId: string; createdAt: string; startedAt: string; endedAt: string | null; promptVersion: string;
  outcome: MissionOutcome | null; reviewStatus: ReviewStatus; reviewAttempts: number;
  split: 'train' | 'validation' | 'test'; trainingApproved: boolean; eventCount: number; humanLabelCount: number; score: number | null; reviewError: string | null;
}
export interface EvalMissionDetail extends EvalMissionSummary {
  playerFeedback?: EvalPlayerFeedback | null;
  consentVersion: string; events: EvalEvent[]; finalState: Record<string, unknown> | null;
  review: EvalReview | null; reviewHistory: EvalReview[]; humanLabels: EvalHumanLabel[];
}
export interface EvalBenchmarkRun {
  id: string; createdAt: string; promptVersion: string; suiteVersion: string; split: string;
  examples: number; passed: number; score: number | null; source: string;
}
export interface EvalPublicSummary {
  /** Recorded voice profiles are separate from configured defaults and text-tool benchmarks. */
  voicePipelines?: EvalVoicePipelineCohort[];
  modelRoles?: {
    task: {provider: 'openai' | 'openrouter'; model: string; evaluationMode: 'text-tool-proxy'};
    reflection: {provider: 'openai' | 'openrouter'; model: string};
    judge: {provider: 'openai' | 'openrouter'; model: string};
    scoring: 'deterministic-fixtures';
  };
  schemaVersion: 1; enabled: boolean; updatedAt: string;
  missionCounts: {total: number; completed: number; failed: number; abandoned: number; reviewed: number; pending: number; reviewErrors: number};
  dimensions: {dimension: EvalDimension; pass: number; fail: number; unknown: number; score: number | null}[];
  timeline: {date: string; reviewed: number; score: number | null}[];
  provenance: {rubricVersion: string; judgeProvider: 'openai' | 'openrouter'; judgeModel: string; observational: true; humanLabelCount: number; reviewCohorts: {rubricVersion: string; judgeProvider: 'openai' | 'openrouter'; judgeModel: string; missions: number}[]};
  benchmarkActivity: {running: boolean; kind: 'baseline' | 'gepa' | null; startedAt: string | null};
  benchmarks: unknown[];
}
export function evalScore(values: EvalValue[]): number | null {
  const known = values.filter(value => value !== 'unknown');
  return known.length ? known.filter(value => value === 'pass').length / known.length : null;
}
export interface EvalVoicePipelineCohort {
  identity: VoicePipelineIdentity; sessions: number;
  stages: {
    stage: VoiceMetricStage; samples: number; cancelled: number; errors: number;
    latency: {metric: typeof VOICE_LATENCY_FIELDS[number]; samples: number; missing: number; p50Ms: number | null; p95Ms: number | null}[];
    usage: VoiceStageUsage;
    usageCoverage: Record<keyof VoiceStageUsage, {samples: number; missing: number}>;
    estimatedCostUsd: number | null;
  }[];
  cost: {estimatedProviderCostUsd: number | null; vpsCostUsd: null; totalCostUsd: null};
  coverage: 'sdk-observations-only';
}

export interface EvalTrainingFeedback {
  /** Player self-reports are reflection input only, never scored turn evidence. */
  kind?: 'reviewed-turn' | 'player-feedback'; playerFeedback?: EvalPlayerFeedback;
  missionId: string; turnId: string; promptVersion: string; rubricVersion: string;
  expected: string; observed: string; evidenceIds: string[];
  humanLabels: {dimension: EvalDimension; value: EvalValue; notes: string}[];
}
